import { z } from 'zod';
import {
  CasePhotoSchema,
  PhotoListSchema,
  CompleteWorkResultSchema,
  KitNoteSchema,
  NoteListSchema,
  KitSchema,
  StageHistoryEntrySchema,
  RunningTaskListSchema,
  StartWorkResultSchema,
  StopWorkResultSchema,
  UndoResultSchema,
} from '@contracts/schemas/kit.schema';
import { pageOf } from '@contracts/schemas/common.schema';
import { AssigneeSchema } from '@contracts/schemas/user.schema';
import { BulkResultSchema } from '@contracts/schemas/bulk.schema';
import type { BulkResult } from '@/application/ports/JobOrderRepository';
import type {
  AssignOptions,
  BulkAssignCommand,
  CompleteWorkCommand,
  CompleteWorkResult,
  FlagCommand,
  KitFilter,
  KitRepository,
  Page,
  PauseWorkCommand,
  QcReworkCommand,
  RunningTask,
  StartWorkCommand,
  StartWorkResult,
  StopWorkCommand,
  StopWorkResult,
  UndoResult,
} from '@/application/ports/KitRepository';
import type {
  Assignee,
  CasePhoto,
  Kit,
  KitNote,
  NoteThread,
  Stage,
  StageHistoryEntry,
} from '@/domain';
import { ProblemDetailsSchema, type ProblemDetails } from '@contracts/schemas/common.schema';
import { photoPath } from '@/domain';
import { env } from '@/shared/config/env';
import { makeThumbnail } from '@/shared/image/thumbnail';
import { SaidError } from '@/shared/failure/failure';
import { ApiError, request, send } from '../client';
import { getAccessToken } from '../tokenStore';

/** The problem document on a refused upload, so the dialog shows the server's words. */
async function readProblemOf(response: Response): Promise<ProblemDetails | null> {
  try {
    const parsed = ProblemDetailsSchema.safeParse(await response.json());
    return parsed.success ? parsed.data : null;
  } catch {
    return null;
  }
}

/**
 * Where a case photograph actually lives.
 *
 * The server returns a server relative path, `/api/v1/uploads/P-1042.jpg`.
 * In production that is already correct, because the API and the built app
 * share an origin. In development `apiBaseUrl` names another host and the path
 * has to be re-based onto it, or the browser asks the Vite dev server for a
 * file it has never heard of.
 */
function photoEndpoint(url: string): string {
  const absolute = /^[a-z][a-z0-9+.-]*:\/\//i;
  if (absolute.test(url)) return url;
  if (!absolute.test(env.apiBaseUrl)) return url;

  return new URL(url, env.apiBaseUrl).toString();
}

/** Said when a download fails with no words of the server's own; `strings.viewer.downloadFailed` says the same. */
const DOWNLOAD_FAILED = 'The photograph could not be downloaded.';

/**
 * Adds the small version of a photograph to its upload, as the part
 * `thumbnail`, when the browser can make one. When it cannot, nothing is
 * added and the photograph goes as it always did: the server falls back to the
 * full file. The request of 5 October 2026 (third), section 6.
 *
 * Here and not in the outbox's request builder, which runs in the service
 * worker where there is nothing to draw on: an upload replayed in the
 * background carries no thumbnail, and one replayed by the page comes through
 * this.
 */
async function appendThumbnail(body: FormData, file: File): Promise<void> {
  const thumbnail = await makeThumbnail(file);
  if (thumbnail !== null) body.append('thumbnail', thumbnail, 'thumbnail.jpg');
}

const KitPageSchema = pageOf(KitSchema);
const HistoryListSchema = z.object({ items: z.array(StageHistoryEntrySchema) });
const AssigneeListSchema = z.object({ items: z.array(AssigneeSchema) });

function query(filter: KitFilter): string {
  const params = new URLSearchParams();
  if (filter.stage !== undefined) params.set('stage', filter.stage);
  if (filter.assignedToUserId !== undefined) params.set('assignedToUserId', filter.assignedToUserId);
  if (filter.mine === true) params.set('mine', 'true');
  if (filter.urgent !== undefined) params.set('urgent', String(filter.urgent));
  if (filter.rework !== undefined) params.set('rework', String(filter.rework));
  if (filter.jobOrderNo !== undefined) params.set('jobOrderNo', filter.jobOrderNo);
  if (filter.q !== undefined && filter.q !== '') params.set('q', filter.q);
  if (filter.page !== undefined) params.set('page', String(filter.page));
  if (filter.pageSize !== undefined) params.set('pageSize', String(filter.pageSize));
  if (filter.sort !== undefined) params.set('sort', filter.sort);
  const search = params.toString();
  return search === '' ? '' : `?${search}`;
}

export class HttpKitRepository implements KitRepository {
  async list(filter: KitFilter): Promise<Page<Kit>> {
    return request(`/kits${query(filter)}`, { schema: KitPageSchema }) as Promise<Page<Kit>>;
  }

  async byId(kitId: string): Promise<Kit> {
    return request(`/kits/${kitId}`, { schema: KitSchema }) as Promise<Kit>;
  }

  async byQr(qrCode: string): Promise<Kit> {
    return request(`/kits/by-qr/${encodeURIComponent(qrCode)}`, {
      schema: KitSchema,
    }) as Promise<Kit>;
  }

  async history(kitId: string): Promise<StageHistoryEntry[]> {
    const result = await request(`/kits/${kitId}/history`, { schema: HistoryListSchema });
    return result.items;
  }

  async notes(kitId: string): Promise<KitNote[]> {
    const result = await request(`/kits/${kitId}/notes`, { schema: NoteListSchema });
    return result.items;
  }

  async noteThread(kitId: string): Promise<NoteThread> {
    return request(`/kits/${kitId}/notes`, { schema: NoteListSchema });
  }

  async markNotesRead(kitId: string): Promise<void> {
    await request(`/kits/${kitId}/notes/read`, { method: 'POST' });
  }

  async addNote(
    kitId: string,
    text: string,
    requestId?: string,
    visibleToUserId?: string | null,
  ): Promise<KitNote> {
    return request(`/kits/${kitId}/notes`, {
      method: 'POST',
      body: visibleToUserId == null ? { text } : { text, visibleToUserId },
      schema: KitNoteSchema,
      idempotencyKey: requestId,
    });
  }

  async photos(kitId: string): Promise<CasePhoto[]> {
    const page = await request(`/kits/${kitId}/photos`, { schema: PhotoListSchema });
    return page.items;
  }

  async removePhoto(kitId: string, photoId: string): Promise<void> {
    await request(`/kits/${kitId}/photos/${photoId}`, { method: 'DELETE' });
  }

  /**
   * Multipart, so this one bypasses the JSON client. The file goes untouched:
   * the server takes the location and camera out of it and does not draw it
   * again. The server makes no small version either, so the browser sends one
   * beside it when it can. The request of 5 October 2026 (third), section 6.
   */
  async addPhoto(kitId: string, file: File, requestId?: string): Promise<void> {
    const body = new FormData();
    body.append('file', file);
    await appendThumbnail(body, file);

    const token = getAccessToken();
    const headers: Record<string, string> = {};
    if (token !== null) headers.Authorization = `Bearer ${token}`;
    // So a retry, or the outbox replaying it, stores the photograph once.
    if (requestId !== undefined) headers['Idempotency-Key'] = requestId;
    const response = await send(`${env.apiBaseUrl}/kits/${kitId}/photos`, {
      method: 'POST',
      body,
      credentials: 'include',
      // No Content-Type: the browser sets the multipart boundary itself, and
      // setting it by hand produces a body the server cannot parse.
      headers,
    });

    if (!response.ok) throw new SaidError('The photo could not be attached');
    if (!env.isProduction) CasePhotoSchema.parse(await response.json());
  }

  async workProofs(kitId: string): Promise<CasePhoto[]> {
    const page = await request(`/kits/${kitId}/work-proof`, { schema: PhotoListSchema });
    return page.items;
  }

  /** Multipart, as a case photograph is, with the outbox entry's id as the Idempotency-Key. */
  async addWorkProof(kitId: string, file: File, requestId: string, note?: string): Promise<void> {
    const body = new FormData();
    body.append('file', file);
    if (note !== undefined && note.trim() !== '') body.append('note', note.trim());
    await appendThumbnail(body, file);

    const token = getAccessToken();
    const response = await send(`${env.apiBaseUrl}/kits/${kitId}/work-proof`, {
      method: 'POST',
      body,
      credentials: 'include',
      headers: {
        ...(token === null ? {} : { Authorization: `Bearer ${token}` }),
        'Idempotency-Key': requestId,
      },
    });

    // A refusal carries the server's words, which the dialog shows: not
    // started, or switched off.
    if (!response.ok) throw new ApiError(response.status, await readProblemOf(response), 'The photograph could not be sent');
    if (!env.isProduction) CasePhotoSchema.parse(await response.json());
  }

  /**
   * The bytes of one photograph, fetched with the access token on the request.
   *
   * With the token there, the signature the server put on the address is not
   * needed, and it is left off: the server signs every list afresh, so the
   * signed address of one file is different each time, and the browser's own
   * cache of uploads, keyed by the whole address, filled with copies of the
   * same picture. Without a token the signature is all there is, and it stays.
   * The request of 5 October 2026 (third), section 6.
   */
  async photoBlob(url: string): Promise<Blob> {
    const token = getAccessToken();
    const response = await send(photoEndpoint(token === null ? url : photoPath(url)), {
      credentials: 'include',
      headers: token === null ? {} : { Authorization: `Bearer ${token}` },
    });

    if (!response.ok) throw new SaidError('The photograph could not be loaded');
    return response.blob();
  }

  /**
   * One photograph as a file to save. The request of 5 October 2026 (third),
   * section 6.
   *
   * Not through the JSON client: the answer is the file. A refusal carries the
   * server's words, which the viewer shows as they arrive. Never from a cache:
   * the server writes a row in the log for each one it answers.
   */
  async downloadPhoto(photoId: string): Promise<Blob> {
    const token = getAccessToken();
    let response: Response;
    try {
      response = await send(`${env.apiBaseUrl}/photos/${encodeURIComponent(photoId)}/download`, {
        credentials: 'include',
        cache: 'no-store',
        headers: token === null ? {} : { Authorization: `Bearer ${token}` },
      });
    } catch {
      throw new SaidError(DOWNLOAD_FAILED);
    }

    if (!response.ok) throw new ApiError(response.status, await readProblemOf(response), DOWNLOAD_FAILED);
    return response.blob();
  }

  /** What this person has running, for seeding the clocks on load. Section 1.5. */
  async running(): Promise<RunningTask[]> {
    const result = await request('/kits/running', { schema: RunningTaskListSchema });
    return result.items;
  }

  async start(command: StartWorkCommand): Promise<StartWorkResult> {
    return request(`/kits/${command.kitId}/start`, {
      method: 'POST',
      idempotencyKey: command.requestId,
      body: {
        startedAt: command.startedAt,
        source: command.source,
        ...(command.takeOver === true ? { takeOver: true } : {}),
      },
      schema: StartWorkResultSchema,
    }) as Promise<StartWorkResult>;
  }

  /*
   * Pause and Mark done carry no elapsed count. Section 1.4 of the request of
   * 20 September 2026: the server computes the entry's seconds from its own
   * timestamps, and a figure from a device is not a second opinion it needs.
   */
  async pause(command: PauseWorkCommand): Promise<Kit> {
    return request(`/kits/${command.kitId}/pause`, {
      method: 'POST',
      idempotencyKey: command.requestId,
      body: {},
      schema: KitSchema,
    }) as Promise<Kit>;
  }

  async stop(command: StopWorkCommand): Promise<StopWorkResult> {
    return request(`/kits/${command.kitId}/stop`, {
      method: 'POST',
      idempotencyKey: command.requestId,
      schema: StopWorkResultSchema,
    }) as Promise<StopWorkResult>;
  }

  async complete(command: CompleteWorkCommand): Promise<CompleteWorkResult> {
    return request(`/kits/${command.kitId}/complete`, {
      method: 'POST',
      idempotencyKey: command.requestId,
      body: {
        completedAt: command.completedAt,
        nextStage: command.nextStage,
        // Section 5: handed to a named person at the destination, or nobody.
        assignToUserId: command.assignToUserId ?? null,
        // Lab Notes, optional. Request of 4 October 2026, section 5.
        ...(command.note == null || command.note.trim() === '' ? {} : { note: command.note.trim() }),
      },
      schema: CompleteWorkResultSchema,
    }) as Promise<CompleteWorkResult>;
  }

  async markUrgent(kitId: string, urgent: boolean, reason?: string): Promise<Kit> {
    return request(`/kits/${kitId}/urgent`, {
      method: 'POST',
      body: reason === undefined || reason === '' ? { urgent } : { urgent, reason },
      schema: KitSchema,
    }) as Promise<Kit>;
  }

  async qcPass(kitId: string): Promise<Kit> {
    return request(`/kits/${kitId}/qc/pass`, {
      method: 'POST',
      schema: KitSchema,
    }) as Promise<Kit>;
  }

  async qcRework(command: QcReworkCommand): Promise<Kit> {
    return request(`/kits/${command.kitId}/qc/rework`, {
      method: 'POST',
      idempotencyKey: command.requestId,
      body: {
        toStage: command.toStage,
        note: command.note,
        // The technician it is for, when one was chosen. Section 8 of the request of 5 October 2026.
        ...(command.assignToUserId == null ? {} : { assignToUserId: command.assignToUserId }),
      },
      schema: KitSchema,
    }) as Promise<Kit>;
  }

  /** Not queued offline: an undo that arrives an hour later is not an undo. */
  async undo(undoId: string): Promise<UndoResult> {
    return request(`/work-undo/${encodeURIComponent(undoId)}`, {
      method: 'POST',
      schema: UndoResultSchema,
    }) as Promise<UndoResult>;
  }

  async assign(kitId: string, userId: string | null, options: AssignOptions = {}): Promise<Kit> {
    const note = options.note?.trim() ?? '';
    return request(`/kits/${kitId}/assign`, {
      method: 'POST',
      body: {
        userId,
        ...(note === '' ? {} : { note, noteVisibility: options.noteVisibility ?? 'everyone' }),
        ...(options.forwardNoteIds === undefined || options.forwardNoteIds.length === 0
          ? {}
          : { forwardNoteIds: options.forwardNoteIds }),
      },
      schema: KitSchema,
    }) as Promise<Kit>;
  }

  async setSpecification(kitId: string, patch: { ingot?: string; block?: string }): Promise<Kit> {
    return request(`/kits/${kitId}/specification`, {
      method: 'PATCH',
      body: patch,
      schema: KitSchema,
    }) as Promise<Kit>;
  }

  /**
   * Who may be given this kit. `stage` asks about a bench the case is going to
   * rather than the one it is on. Section 5.3 of the request of 20 September 2026.
   */
  async assignees(kitId: string, stage?: Stage): Promise<Assignee[]> {
    const query = stage === undefined ? '' : `?stage=${stage}`;
    const result = await request(`/kits/${kitId}/assignees${query}`, {
      schema: AssigneeListSchema,
    });
    return result.items;
  }

  /** Whoever works a bench, for the picker on a selection. Section 4 of the request of 24 September 2026. */
  async stageAssignees(stage: Stage): Promise<Assignee[]> {
    const result = await request(`/stages/${stage}/assignees`, { schema: AssigneeListSchema });
    return result.items;
  }

  /** Not queued offline, as the other commands on a selection are not. */
  async assignMany(command: BulkAssignCommand): Promise<BulkResult> {
    return request('/kits/bulk/assign', {
      method: 'POST',
      body: command,
      schema: BulkResultSchema,
    });
  }

  async flag(command: FlagCommand): Promise<CompleteWorkResult> {
    return request(`/kits/${command.kitId}/flag`, {
      method: 'POST',
      idempotencyKey: command.requestId,
      body: {
        toStage: command.toStage,
        note: command.note,
        assignToUserId: command.assignToUserId ?? null,
      },
      schema: CompleteWorkResultSchema,
    }) as Promise<CompleteWorkResult>;
  }
}
